export type MetricType = "bytes" | "percent" | "temperature" | "generic";

const ACRONYMS: Record<string, string> = {
    cpu: "CPU",
    ram: "RAM",
    gpu: "GPU",
    io: "I/O",
    ip: "IP",
    id: "ID",
    iowait: "I/O wait",
    irq: "IRQ",
};

export const CHART_COLORS = ["#FFC713", "#344a5e", "#62b33e", "#0fa8bf", "#f79f11", "#d32f2f"];

export function getTimeFormat(timestamps: number[]): string {
    if (timestamps.length < 2) return "HH:mm:ss";

    const sorted = [...timestamps].sort((a, b) => a - b);
    const rangeMs = sorted[sorted.length - 1] - sorted[0];
    const rangeHours = rangeMs / (1000 * 60 * 60);

    return rangeHours > 24 ? "MMM D HH:mm" : "HH:mm:ss";
}

export function camelCaseToTitleCase(str: string): string {
    if (!str) return str;
    const result = str
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (char) => char.toUpperCase())
        .trim();

    // Replace known acronyms with their uppercase versions
    return result
        .split(" ")
        .map((word) => ACRONYMS[word.toLowerCase()] || word)
        .join(" ");
}

export function detectMetricType(name: string, categoryHint?: string): MetricType {
    const lowerName = name.toLowerCase();
    const lowerCategory = (categoryHint || "").toLowerCase();

    if (lowerName.includes("ram") || 
        lowerName.includes("memory") || 
        lowerName.includes("bytes") || 
        lowerName.includes("swap")) {
        return "bytes";
    }
    if (lowerCategory.includes("cpu") || 
        lowerName.includes("busy") || 
        lowerName.includes("idle") || 
        lowerName.includes("percent") || 
        lowerName.includes("usage")) {
        return "percent";
    }

    if (lowerName.includes("temp")) {
        return "temperature";
    }
    return "generic";
}

export function formatBytes(value: number, useShortVersion = false): string {
    if (value >= 1e9) return useShortVersion ? `${(value / 1e9).toFixed(1)}G` : `${(value / 1e9).toFixed(2)} GB`;
    if (value >= 1e6) return useShortVersion ? `${(value / 1e6).toFixed(1)}M` : `${(value / 1e6).toFixed(2)} MB`;
    if (value >= 1e3) return useShortVersion ? `${(value / 1e3).toFixed(1)}K` : `${(value / 1e3).toFixed(2)} KB`;
    return useShortVersion ? `${value}` : `${value.toFixed(0)} B`;
}

export function formatPercent(value: number, short = false): string {
    // Handle 0-1 scale (multiply by 100 to get percentage)
    const pct = value <= 1 ? value * 100 : value;
    return short ? `${pct.toFixed(0)}%` : `${pct.toFixed(1)}%`;
}

export function formatTemperature(value: number): string {
    return `${value.toFixed(1)}°C`;
}

export function formatNumber(value: number, short = false): string {
    if (short) {
        if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
        if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
        return value.toString();
    }
    if (Number.isInteger(value)) {
        return value.toString();
    }
    return value.toFixed(2);
}

function formatByType(value: number, metricType: MetricType, short: boolean): string {
    switch (metricType) {
        case "bytes":
            return formatBytes(value, short);
        case "percent":
            return formatPercent(value, short);
        case "temperature":
            return formatTemperature(value);
        default:
            return formatNumber(value, short);
    }
}

export function formatMetricValue(value: number, nameOrType: string, categoryHint?: string): string {
    return formatByType(value, detectMetricType(nameOrType, categoryHint), false);
}

export function formatYAxisValue(value: number, categoryHint: string): string {
    return formatByType(value, detectMetricType(categoryHint, categoryHint), true);
}
